import { useEffect, useState } from 'react';
import {
  QuestionnaireFormProps,
  QuestionnaireQuestions,
} from '../../../models';
import { Header } from '../../Screen';

type RadioOption =
  | string
  | {
      title: string;
      description: string;
    };

export type RadioFormProps<
  Questionnaire extends QuestionnaireQuestions,
  ValueType extends string
> = {
  mapValue: Record<ValueType, RadioOption>;
} & QuestionnaireFormProps<Questionnaire, ValueType>;

export const RadioForm = <
  Questionnaire extends QuestionnaireQuestions,
  ValueType extends string
>({
  value,
  question,
  mapValue,
  onSubmitValue,
  featureName,
  setValidForSubmission,
}: RadioFormProps<Questionnaire, ValueType>) => {
  const [radioValue, setRadioValue] = useState<ValueType | undefined>(value);

  const handleSubmit = () => {
    if (!radioValue) {
      throw new Error(
        `[${featureName}] Radio value is missing for submission from following options: ${Object.keys(
          mapValue
        ).join(', ')} for question: ${question}`
      );
    }
    onSubmitValue(radioValue);
  };

  useEffect(() => {
    setValidForSubmission(Boolean(radioValue));
  }, [radioValue, setValidForSubmission]);

  const entries = Object.entries(mapValue) as [ValueType, RadioOption][];

  return (
    <Header onSubmit={handleSubmit}>
      <div className="p-label-container">
        {entries.map(([v, label], index) => (
          <div className={index !== 0 ? 'mt8' : ''} key={v}>
            <input
              id={v}
              className="p-radio"
              type="radio"
              value={v}
              onChange={() => setRadioValue(v as ValueType)}
              checked={radioValue === v}
            />
            <label
              htmlFor={v}
              className="p-label p-label--bordered"
              data-cy={`radio-${v}`}
            >
              {typeof label === 'string' ? (
                label
              ) : (
                <div>
                  <p className="p-p">{label.title}</p>
                  <span className="d-block p-p p-p--small tc-grey-600">
                    {label.description}
                  </span>
                </div>
              )}
            </label>
          </div>
        ))}
      </div>
    </Header>
  );
};
